import React, { useState, useEffect, useMemo } from "react";
import * as R from "ramda";
import styled from "styled-components";
import { connect } from "react-redux";
import { Icon } from "antd";
import { useTranslation } from "react-i18next";
import { Prompt } from "react-router-dom";

import { isLoading } from "../../../utils/actions";
import {
  UPDATE_MATCH_NOTES,
  updateMatchNotes,
  createMatchNotes,
  CREATE_MATCH_NOTES
} from "../../../store/actions/candidates";
import Button from "../../Button";

const noteMaxLength = 10000;

const TextArea = styled.textarea`
  width: 100%;
  border: 1px solid
    ${props => (props.historyMode ? "transparent" : "rgba(0, 0, 0, 0.2)")};
  border-radius: 3px;
  padding: 4px 6px;
  background: ${props => (props.historyMode ? "white" : "#f7f9fa")};
  min-height: 120px;
  resize: none;
  outline: 0;
  border-bottom: 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  transition: 0.3s ease;
  &:focus {
    ${props =>
      props.historyMode &&
      `
      background: #f7f9fa;
      border-color: rgba(0, 0, 0, 0.2)
      `}
  }
`;

const WrapperButton = styled.div`
  border: ${props =>
    props.historyMode
      ? "1px solid transparent"
      : "1px solid rgb(208, 214, 218)"};
  height: ${props => (props.historyMode ? "inherit" : "50px")};
  display: flex;
  background: ${props => (props.historyMode ? "white" : "rgb(247, 249, 250)")};
  justify-content: flex-end;
  border-radius: 3px;
  width: 100%;
  border-top: none;
  border-radius: 0 0 3px 3px;
  transition: 0.3s ease;
  z-index: 1;
  .ant-btn-loading {
    opacity: 1 !important;
  }
  ${TextArea}:focus ~ & {
    background: rgb(247, 249, 250);
    border: 1px solid rgb(208, 214, 218);
    border-top: none;
    button {
      opacity: 1;
    }
  }
  ${props =>
    props.historyMode &&
    `
    button {
      font-size: 10px;
      margin: 5px !important;
      padding: 0 5px;
      height: 20px;
      opacity: 0;
      &:active {
        opacity: 1;
      }
    }
  `}
`;

const Length = styled.span`
  color: #9ca8b1;
  position: absolute;
  font-size: ${props => (props.historyMode ? "12px" : "14px")};
  top: ${props => (props.historyMode ? "-15px" : 0)};
  right: 0;
  transition: 0.3s ease;
  ${TextArea}:focus ~ & {
    opacity: 1;
  }
  opacity: ${props => (props.historyMode ? 0 : 1)};
`;

const Label = styled.label`
  color: #3d64ff;
  font-size: 10px;
  display: inline-flex;
  align-items: center;
  z-index: 9;
  transition: 0.3s ease;
  position: absolute;
  left: 0;
  top: 7px;
  cursor: pointer;
  .anticon-edit {
    font-size: 14px;
  }
  ${TextArea}:focus ~ ${WrapperButton} &,
  .ant-btn-loading ~ & {
    opacity: 0;
    transform: translateY(5px);
  }
`;

const Notes = ({
  candidate,
  updateMatchNotes,
  createMatchNotes,
  creatingNotes,
  updatingNotes,
  stepId,
  historyMode,
  forKey,
  idNoteLoading,
  isActive,
  opportunityId
}) => {
  const { t } = useTranslation();

  const currentNote = R.find(R.propEq("idStep", stepId))(
    R.values(candidate.notes)
  );
  const [note, setNote] = useState(currentNote);

  useEffect(() => {
    if (currentNote) {
      setNote(currentNote);
    }
  }, [currentNote]);

  const savingNotes =
    (creatingNotes || updatingNotes) && note && idNoteLoading === note.id;

  const saveNote = async text => {
    currentNote
      ? await updateMatchNotes({
          idStep: stepId,
          id: currentNote.id,
          formData: { text },
          opportunityId
        })
      : await createMatchNotes({
          idStep: stepId,
          formData: { text, idCandidate: candidate.id },
          opportunityId
        });
  };

  const noteText = R.propOr("", "text", note);

  const unsavedChanges = useMemo(() => {
    return R.propOr("", "text", currentNote) !== noteText;
  }, [currentNote, noteText]);

  return (
    <>
      <TextArea
        data-test="text-area-notes"
        rows={1}
        id={forKey}
        autoSize={{ minRows: 1, maxRows: 4 }}
        onChange={e => {
          setNote(R.assoc("text", e.target.value, note));
        }}
        value={noteText}
        historyMode={historyMode}
        maxLength={noteMaxLength}
      />
      <Length historyMode={historyMode}>
        {`${R.length(noteText)} / ${noteMaxLength}`}
      </Length>
      <WrapperButton historyMode={historyMode}>
        <Button
          data-test="save-notes-button"
          type="primary"
          style={{ marginRight: 8, marginTop: 8 }}
          onClick={() => {
            saveNote(note.text);
          }}
          loading={savingNotes}
          disabled={
            R.length(noteText) > noteMaxLength || R.length(noteText) === 0
          }
        >
          {t("opportunity:process.candidateDetails.saveNote")}
        </Button>
        {historyMode && (
          <Label htmlFor={forKey}>
            <Icon type="edit" />
            {t("opportunity:process.candidateDetails.history.edit")}
          </Label>
        )}
        <Prompt
          message={t("opportunity:process.candidateDetails.areYouSure")}
          when={unsavedChanges && !!isActive}
        />
      </WrapperButton>
    </>
  );
};

const enhancer = connect(
  state => ({
    candidate: R.defaultTo({}, state.candidates.selected),
    updatingNotes: isLoading(UPDATE_MATCH_NOTES, state),
    creatingNotes: isLoading(CREATE_MATCH_NOTES, state),
    idNoteLoading: state.candidates.idNoteLoading
  }),
  {
    updateMatchNotes,
    createMatchNotes
  }
);

export default enhancer(Notes);
